import { supabase } from '@/integrations/supabase/client';
import { CHAT_ROLE } from '@/utils/ChatUtils';
import type { Conversation, Message } from '@/types/chat';

export class ConversationService {
  static async checkExistingConversation(
    counterpartId: string,
    userRole: string,
    userId: string,
    currentCommunityId: string,
    userBusinessId?: string | null
  ): Promise<Conversation | null> {
    try {
      let businessId: string;
      let memberId: string;
      
      if (userRole === CHAT_ROLE.COMMUNITY_MEMBER) {
        businessId = counterpartId;
        memberId = userId;
      } else {
        if (!userBusinessId) {
          console.error('[ConversationService] No business found for current user');
          return null;
        }
        
        businessId = userBusinessId;
        memberId = counterpartId;
      }
      
      const { data, error } = await supabase
        .from('conversations')
        .select(`
          *,
          business:business_id (
            id,
            name,
            company_logo
          ),
          member:member_id (
            id,
            firstname,
            lastname,
            email,
            avatar_url
          )
        `)
        .eq('business_id', businessId)
        .eq('member_id', memberId)
        .eq('community_id', currentCommunityId)
        .maybeSingle();
      
      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }
      
      // Fetch username if conversation exists
      if (data && data.member_id) {
        const { data: memberData } = await supabase
          .from('community_members')
          .select('username')
          .eq('user_id', data.member_id)
          .eq('community_id', data.community_id)
          .single();
        
        if (memberData && data.member) {
          (data.member as any).username = memberData.username;
        }
      }
      
      return data as Conversation;
    } catch (error) {
      console.error('Error checking existing conversation:', error);
      return null;
    }
  }

  static async createConversation(
    counterpartId: string,
    userRole: string,
    userId: string,
    currentCommunityId: string,
    userBusinessId: string
  ): Promise<Conversation> {
    let businessId: string;
    let memberId: string;
    
    if (userRole === CHAT_ROLE.COMMUNITY_MEMBER) {
      businessId = counterpartId;
      memberId = userId;
    } else {
      businessId = userBusinessId;
      memberId = counterpartId;
    }
    
    const { data: conversationData, error: conversationError } = await supabase
      .from('conversations')
      .insert({
        business_id: businessId,
        member_id: memberId,
        community_id: currentCommunityId,
        status: 'active'
      })
      .select('*')
      .single();
    
    if (conversationError) {
      throw conversationError;
    }
    
    return conversationData as Conversation;
  }

  static async loadConversationData(conversationId: string, communityId: string): Promise<{
    conversation: Conversation;
    messages: Message[];
  }> {
    console.log(`[ConversationService] Loading conversation data for ID: ${conversationId}`);
    
    try {
      // Load conversation details
      const { data: conversationData, error: conversationError } = await supabase
        .from('conversations')
        .select(`
          *,
          business:business_id (
            id,
            name,
            company_logo
          ),
          member:member_id (
            id,
            firstname,
            lastname,
            email,
            avatar_url
          )
        `)
        .eq('id', conversationId)
        .eq('community_id', communityId)
        .single();

      if (conversationError) {
        console.error('[ConversationService] Error loading conversation:', conversationError);
        throw conversationError;
      }

      if (!conversationData) {
        throw new Error('Conversation not found');
      }

      // Get username for the member
      const { data: memberUsername } = await supabase
        .from('community_members')
        .select('username')
        .eq('user_id', conversationData.member_id)
        .eq('community_id', communityId)
        .single();

      // Add username to member data
      if (conversationData.member && memberUsername) {
        (conversationData.member as any).username = memberUsername.username;
      }

      // Load messages with images (limit to 50 most recent)
      const { data: messagesData, error: messagesError } = await supabase
        .from('messages')
        .select(`
          *,
          images:message_images (
            id,
            message_id,
            image_url,
            original_filename,
            file_size,
            mime_type,
            display_order,
            created_at,
            updated_at
          )
        `)
        .eq('conversation_id', conversationId)
        .order('sent_at', { ascending: false })
        .limit(50);

      if (messagesError) {
        console.error('[ConversationService] Error loading messages:', messagesError);
        throw messagesError;
      }

      // Reverse messages to display in ascending order (oldest first)
      // since we fetched them in descending order with limit
      const reversedData = (messagesData || []).reverse();
      
      const messages = reversedData.map(msg => ({
        ...msg,
        images: msg.images || []
      }));

      console.log(`[ConversationService] Successfully loaded ${messages.length} messages with images`);

      return {
        conversation: conversationData as Conversation,
        messages: messages as Message[]
      };
    } catch (error) {
      console.error('[ConversationService] Error in loadConversationData:', error);
      throw error;
    }
  }

  static async markMessagesAsRead(conversationId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('messages')
      .update({ read_at: new Date().toISOString() })
      .eq('conversation_id', conversationId)
      .eq('receiver_id', userId)
      .is('read_at', null);
    
    if (error) {
      console.error('[ConversationService] Error marking messages as read:', error);
      throw error;
    }
  }

  static async sendMessage(conversationId: string, senderId: string, receiverId: string, content: string): Promise<Message> {
    const { data, error } = await supabase
      .from('messages')
      .insert({
        conversation_id: conversationId,
        sender_id: senderId,
        receiver_id: receiverId,
        content: content
      })
      .select()
      .single();
    
    if (error) {
      throw error;
    }
    
    return data as Message;
  }

  static async getUnreadCount(userId: string): Promise<number> {
    const { count, error } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .eq('receiver_id', userId)
      .is('read_at', null);
    
    if (error) {
      throw error;
    }
    
    return count || 0;
  }

  static async getUnreadCountForRole(userId: string, userRole: 'member' | 'business', userBusinessId?: string): Promise<number> {
    if (userRole === 'member') {
      // For community members, count unread messages in conversations where they are the member
      const { count, error } = await supabase
        .from('messages')
        .select(`
          *,
          conversation:conversations!inner(
            member_id
          )
        `, { count: 'exact', head: true })
        .eq('receiver_id', userId)
        .eq('conversation.member_id', userId)
        .is('read_at', null);
      
      if (error) {
        throw error;
      }
      
      return count || 0;
    } else {
      // For business owners, count unread messages in conversations where they are the business owner
      if (!userBusinessId) {
        return 0; // No business ID means no business conversations
      }
      
      const { count, error } = await supabase
        .from('messages')
        .select(`
          *,
          conversation:conversations!inner(
            business_id
          )
        `, { count: 'exact', head: true })
        .eq('receiver_id', userId)
        .eq('conversation.business_id', userBusinessId)
        .is('read_at', null);
      
      if (error) {
        throw error;
      }
      
      return count || 0;
    }
  }
}
